import React, { useEffect, useState, useRef } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";import { PageLoader } from '@/components/ui/page-loader';import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { BASE_URL } from '../config/constants';
import { toast } from 'sonner';
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

const CONTENT_POSITIONS = [
  { value: 'center-left', label: 'Center-left', preview: 'items-center justify-start' },
  { value: 'center-center', label: 'Center-center', preview: 'items-center justify-center' },
  { value: 'center-right', label: 'Center-right', preview: 'items-center justify-end' },
  { value: 'bottom-left', label: 'Bottom-left', preview: 'items-end justify-start' },
  { value: 'bottom-center', label: 'Bottom-center', preview: 'items-end justify-center' },
  { value: 'bottom-right', label: 'Bottom-right', preview: 'items-end justify-end' },
] as const;
type HeroContentPosition = typeof CONTENT_POSITIONS[number]['value'];
const contentPosition = (value: unknown): HeroContentPosition =>
  CONTENT_POSITIONS.find((position) => position.value === value)?.value ?? 'center-center';

interface HeroSection {
  _id: string;
  mainTitle: string;
  subtitle1: string;
  subtitle2: string;
  description: string;
  musicControls: boolean;
  socialMediaLinks: string[];
  heroImage: string;
  heroImageActive: boolean;
  noImageContentPosition: HeroContentPosition;
  isActive: boolean;
  order: number;
  // audioElement: string;
  // ctaText: string;
  ctaLink?: string;
}

interface HeroSectionManagerProps {
  domain: string;
  siteType?: 'artist' | 'management';
}

const emptyHeroSection: Omit<HeroSection, '_id'> = {
  mainTitle: '',
  subtitle1: '',
  subtitle2: '',
  description: '',
  musicControls: false,
  socialMediaLinks: [],
  heroImage: '',
  heroImageActive: true,
  noImageContentPosition: 'center-center',
  isActive: true,
  order: 0,
  // audioElement: '',
  // ctaText: '',
  ctaLink: '',
};

const HeroSectionManager: React.FC<HeroSectionManagerProps> = ({ domain, siteType = 'artist' }) => {
  const isManagement = siteType === 'management';
  const subject = isManagement ? 'Management' : 'Artist';
  const [heroSection, setHeroSection] = useState<HeroSection | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState(emptyHeroSection);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadStep, setUploadStep] = useState<'idle' | 'selecting' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropPercent, setCropPercent] = useState<any>(null);
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subtitle2Ref = useRef<HTMLTextAreaElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null, minHeight: number = 120) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    }
  };
 
  const fetchHeroSection = () => {
    setLoading(true);
    setLoadFailed(false);
    setError('');
    axios.get(BASE_URL + '/api/hero-section')
      .then(res => {
        const loaded = {
          ...emptyHeroSection,
          ...res.data,
          noImageContentPosition: contentPosition(res.data.noImageContentPosition),
        };
        setHeroSection(res.data._id ? loaded : null);
        setForm(loaded);
      })
      .catch((err) => {
        const message = err.response?.data?.message || 'Failed to load hero section';
        setLoadFailed(true);
        setError(message);
        toast.error(message);
      })
      .finally(() => setLoading(false));
  };

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    setCropPercent(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (form.heroImage) {
      fetch(form.heroImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'hero-image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          toast.error('Failed to load image for editing');
        });
    }
  };

  useEffect(() => {
    if (domain) {
      fetchHeroSection();
    }
  }, [domain]);

  // Adjust textarea heights when content changes (including initial load)
  useEffect(() => {
    adjustTextareaHeight(subtitle2Ref.current, 56);
  }, [form.subtitle2]);

  useEffect(() => {
    adjustTextareaHeight(descriptionRef.current, 80);
  }, [form.description]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validFormats = ['image/png', 'image/jpeg'];
    const maxSize = 2 * 1800 * 2250;
    
    if (!validFormats.includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      toast.error('Only .png and .jpeg files are allowed.');
      return;
    }
    
    if (file.size > maxSize) {
      setError('File size must be less than 2MB.');
      toast.error('File size must be less than 2MB.');
      return;
    }
    
    setError('');
    setSelectedFile(file);
    setUploadStep('cropping');
  };

  const handleCropComplete = async (_croppedImage: string) => {
    // Callback when user adjusts crop - just store the notification that we're ready
    // The actual upload happens when user clicks the Upload button
  };

  const hasChanges = (): boolean => {
    if (!heroSection) return true;
    return JSON.stringify(form) !== JSON.stringify(heroSection);
  };

  const normalizeUrl = (input: string): { url: string; suggestion: string | null } => {
    if (!input.trim()) {
      return { url: '', suggestion: null };
    }

    const trimmed = input.trim().replace(/^https?:\/\//, '');

    if (!trimmed) {
      return { url: '', suggestion: null };
    }

    const urlWithProtocol = `https://${trimmed}`;

    try {
      new URL(urlWithProtocol);
      return { url: urlWithProtocol, suggestion: null };
    } catch {
      return { url: urlWithProtocol, suggestion: 'Please enter a valid domain or URL' };
    }
  };

  const handleReset = () => {
    if (heroSection) {
      setForm({ ...heroSection });
    } else {
      setForm(emptyHeroSection);
    }
    resetUploadState();
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { url } = normalizeUrl(form.ctaLink || '');
      const submitData = { ...form, ctaLink: url };
      
      if ('_id' in submitData) {
        delete (submitData as any)._id;
      }
      
      let savedData;
      if (heroSection?._id) {
        const res = await axios.put(`${BASE_URL}/api/hero-section/${heroSection._id}`, submitData);
        savedData = res.data.data || res.data;
      } else {
        const res = await axios.post(BASE_URL + '/api/hero-section', submitData);
        savedData = res.data.data || res.data;
      }
      // Optimistically update local state instead of re-fetching
      if (savedData?._id) {
        const saved = { ...savedData, noImageContentPosition: contentPosition(savedData.noImageContentPosition) };
        setHeroSection(saved);
        setForm(saved);
      }
      resetUploadState();
      toast.success('Hero section saved successfully!');
      // Trigger preview refresh after successful save

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save hero section';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      
      {/* Header */}
      <div className="mb-4">
        <h2 className="page-title">Hero Section</h2>
      </div>

      <div className="bg-card rounded-lg border p-3 sm:p-4 mb-6">
        <p className="text-sm text-muted-foreground">
          Refer to the Live Website on the right to see how the hero section appears on your website. Make sure to save changes to update the live preview. You can also visit <a href='https://demo.artistintro.com/' target='_blank'>https://demo.artistintro.com/</a>
        </p>
      </div>

      {loading ? (
        <PageLoader label="Loading hero..." />
      ) : loadFailed ? (
        <div role="alert" className="bg-card rounded-lg border border-border p-4 space-y-3">
          <p className="text-danger">{error}</p>
          <Button type="button" variant="outline" onClick={fetchHeroSection}>Retry loading hero</Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form Fields */}
            <div className="space-y-5">
              <div className="form-group">
                <Label htmlFor="hero-name" className="form-label">{subject} Name</Label>
                <Input 
                  id="hero-name"
                  name="mainTitle" 
                  placeholder={isManagement ? 'Management Company Name' : 'Artist Name or Band Name'}
                  value={form.mainTitle} 
                  onChange={handleFormChange} 
                />
                <p className="text-xs text-muted-foreground">
          Optional. Leave blank to hide the main heading.
        </p>
              </div>

              <div className="form-group">
                <Label className="form-label">{isManagement ? 'Management Specialization' : "Artist's Profession"}</Label>
                <Input 
                  name="subtitle1" 
                  placeholder={isManagement ? 'Artist management, bookings, promotions, etc.' : 'Composer, Singer, Lyricist, etc.'}
                  value={form.subtitle1} 
                  onChange={handleFormChange} 
                />
              </div>

              <div className="form-group">
                <Label className="form-label">Define {subject}</Label>
                <Textarea 
                  ref={subtitle2Ref}
                  name="subtitle2" 
                  placeholder={isManagement ? 'Services, strengths, or what makes your company unique' : 'Achievements, style, or a unique tagline'}
                  value={form.subtitle2} 
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, subtitle2: e.target.value }));
                    adjustTextareaHeight(subtitle2Ref.current, 56);
                  }}
                  className="sm:resize-y min-h-[56px]" 
                  rows={2}
                />
              </div>

              <div className="form-group">
                <Label className="form-label">{subject}'s Short Description</Label>
                <Textarea 
                  ref={descriptionRef}
                  name="description" 
                  placeholder={`Enter ${isManagement ? "management company's" : "artist's"} short description (line breaks will be preserved as paragraphs)`}
                  value={form.description} 
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, description: e.target.value }));
                    adjustTextareaHeight(descriptionRef.current, 80);
                  }}
                  className="sm:resize-y min-h-[80px]" 
                />
                
              </div>

              {/* <div className="form-group">
                <Label className="form-label">CTA Text</Label>
                <Input 
                  name="ctaText" 
                  placeholder="Enter CTA text" 
                  value={form.ctaText} 
                  onChange={handleFormChange} 
                />
              </div> */}

              {/* <div className="form-group">
                <Label className="form-label">CTA Link</Label>
                <div className="flex items-center border border-border rounded-lg overflow-hidden transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary hover:border-muted-foreground/30">
                  <span className="bg-secondary px-3.5 py-2 text-foreground font-medium text-sm">https://</span>
                  <input 
                    name="ctaLink" 
                    value={(form.ctaLink || '').replace(/^https?:\/\//, '')} 
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/^https?:\/\//, '');
                      setForm(prev => ({ ...prev, ctaLink: rawValue }));
                    }}
                    className="flex-1 border-0 px-3.5 py-2 outline-none bg-transparent text-sm" 
                    placeholder="e.g., google.com or example.com/path"
                  />
                </div>
              </div> */}

              {/* <div className="form-group">
                <Label className="form-label">Audio Element URL</Label>
                <Input 
                  name="audioElement" 
                  placeholder="Enter audio URL" 
                  value={form.audioElement} 
                  onChange={handleFormChange} 
                />
              </div> */}

              {/* <div className="flex items-center space-x-3">
                <Switch
                  checked={form.musicControls}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, musicControls: checked }))}
                  aria-label="Toggle music controls"
                />
                <span className={form.musicControls ? 'status-label-active' : 'status-label-inactive'}>
                  {form.musicControls ? 'Music Controls Visible' : 'Music Controls Hidden'}
                </span>
              </div> */}
            </div>

            {/* Right Column - Status and Image */}
            <div className="space-y-5">
              <div className="form-group">
                <div className="flex items-center justify-between">
                  <Label className="form-label">Hero Image</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={form.heroImageActive}
                      onCheckedChange={(checked) => setForm(prev => ({ ...prev, heroImageActive: checked }))}
                      aria-label="Toggle hero image visibility"
                    />
                    <span className={form.heroImageActive ? 'status-label-active' : 'status-label-inactive'}>
                      {form.heroImageActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                {uploadStep === 'idle' ? (
                  !form.heroImage ? (
                    <div className="w-full">
                      <Card
                        className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('bg-muted/50');
                        }}
                        onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                          e.currentTarget.classList.remove('bg-muted/50');
                        }}
                        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('bg-muted/50');
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            const event = {
                              target: { files }
                            } as any;
                            handleFileChange(event);
                          }
                        }}
                        onClick={openFileUpload}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary hover:text-primary/80 p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.jpg,.jpeg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: JPG, PNG (max 2 MB) | Aspect ratio: 1:1.25
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={openCropperForExisting}
                      >
                        <div 
                          className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                          style={{
                            backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                            backgroundSize: '16px 16px',
                            backgroundPosition: '0 0, 8px 8px',
                            backgroundRepeat: 'repeat'
                          }}
                        >
                          <img 
                            src={form.heroImage} 
                            alt="uploaded" 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={() => setForm(prev => ({ ...prev, heroImage: '' }))}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="hidden"
                      />
                    </div>
                  )
                ) : uploadStep === 'cropping' && selectedFile ? (
                  <div className="space-y-2">
                      <Card className="flex flex-col items-center border border-border">
                        <div className="w-full border-b border-border p-3">
                          <p className="text-sm font-medium text-foreground text-center">Adjust crop area (1:1.25 Aspect) - Drag to move, edges to resize</p>
                        </div>
                      <div className="w-full flex justify-center py-3">
                        <Cropper
                          file={selectedFile}
                          aspect={1 / 1.25}
                          maxImageSize={2 * 1024 * 1024}
                          keepSelection
                          onComplete={(_pixelCrop, percentCrop) => {
                            setCropPercent(percentCrop);
                          }}
                          onCrop={handleCropComplete}
                          onCropRef={cropperRef}
                        />
                      </div>
                    </Card>
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        onClick={async () => {
                          if (!cropPercent || !selectedFile) return;
                          
                          setUploading(true);
                          try {
                            // Load original image at full resolution
                            const originalUrl = URL.createObjectURL(selectedFile);
                            const originalImg = new Image();
                            
                            await new Promise((resolve, reject) => {
                              originalImg.onload = resolve;
                              originalImg.onerror = reject;
                              originalImg.src = originalUrl;
                            });
                            
                            // Convert percentage crop to pixel coordinates on original image
                            const imgWidth = originalImg.naturalWidth;
                            const imgHeight = originalImg.naturalHeight;
                            
                            const cropX = (cropPercent.x / 100) * imgWidth;
                            const cropY = (cropPercent.y / 100) * imgHeight;
                            const cropWidth = (cropPercent.width / 100) * imgWidth;
                            const cropHeight = (cropPercent.height / 100) * imgHeight;
                            
                            // Create canvas with full-resolution cropped dimensions
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            
                            canvas.width = Math.round(cropWidth);
                            canvas.height = Math.round(cropHeight);
                            
                            // Draw the cropped portion from the original full-resolution image
                            ctx?.drawImage(
                              originalImg,
                              Math.round(cropX),
                              Math.round(cropY),
                              Math.round(cropWidth),
                              Math.round(cropHeight),
                              0,
                              0,
                              Math.round(cropWidth),
                              Math.round(cropHeight)
                            );
                            
                            // Convert to blob
                            const mimeType = selectedFile.type || 'image/png';
                            const croppedBlob = await new Promise<Blob>((resolve) => {
                              canvas.toBlob((blob) => {
                                resolve(blob || new Blob());
                              }, mimeType, 1.0);
                            });
                            
                            URL.revokeObjectURL(originalUrl);
                            
                            // Upload
                            const formData = new FormData();
                            const extension = selectedFile.type === 'image/jpeg' ? 'jpg' : 'png';
                            formData.append('file', croppedBlob, `cropped-hero.${extension}`);
                            
                            const res = await axios.post(BASE_URL + '/api/images/upload', formData, {
                              headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'X-Domain': localStorage.getItem('selectedDomain'),
                              },
                            });
                            
                            setForm((prev) => ({ ...prev, heroImage: res.data.url }));
                            resetUploadState();
                            const isUpdate = heroSection?.heroImage ? true : false;
                            toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
                          } catch (err: any) {
                            console.error('Upload error:', err.response?.data || err.message);
                            setError('Image upload failed.');
                            toast.error(err.response?.data?.message || 'Image upload failed.');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                        size="sm"
                        loading={uploading}
                        loadingText="Uploading..."
                      >
                        {form.heroImage ? 'Update' : 'Upload'}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={resetUploadState}
                        disabled={uploading}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
              <Accordion type="single" collapsible className="rounded-lg border border-border px-3">
                <AccordionItem value="content-position" className="border-none">
                  <AccordionTrigger type="button" id="hero-content-position-heading">
                    Content position - without image
                  </AccordionTrigger>
                  <AccordionContent>
                    <fieldset
                className="space-y-3 disabled:opacity-60"
                disabled={saving || (Boolean(form.heroImage) && form.heroImageActive !== false)}
                aria-labelledby="hero-content-position-heading"
                aria-describedby="hero-content-position-help"
              >
                <div className="grid grid-cols-3 gap-2">
                  {CONTENT_POSITIONS.map(({ value, label, preview }) => (
                    <label key={value} className="relative cursor-pointer">
                      <input
                        type="radio"
                        name="noImageContentPosition"
                        value={value}
                        checked={form.noImageContentPosition === value}
                        onChange={() => setForm((prev) => ({ ...prev, noImageContentPosition: value }))}
                        className="peer sr-only"
                      />
                      <span className="flex flex-col gap-2 rounded-lg border border-border p-2 text-center peer-checked:border-primary peer-checked:bg-primary/10 peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-disabled:cursor-not-allowed">
                        <span aria-hidden="true" className={`flex h-12 rounded border border-border bg-muted/30 p-2 ${preview}`}>
                          <span className="h-2 w-6 rounded-sm bg-primary" />
                        </span>
                        <span className="text-xs">{label}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p id="hero-content-position-help" className="text-xs text-muted-foreground">
                  Applies when the hero image is missing or hidden. Hide or remove the image to change this setting.
                  On mobile, center positions become center-center and bottom positions become bottom-center.
                </p>
                    </fieldset>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>

          {error && <div className="mt-4 p-3 bg-danger-light border border-danger/30 text-danger rounded-lg text-sm">{error}</div>}

          <div className="flex gap-2 mt-8 pt-6 border-t border-border">
            <Button type="submit" disabled={!hasChanges() || saving} loading={saving} loadingText="Saving...">
              Save Changes
            </Button>
            <Button type="button" variant="outline" onClick={handleReset} disabled={saving}>
              Reset
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default HeroSectionManager;
